import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Player, type SessionView } from "../api.js";

/**
 * FR-ALLOC-11, FR-ALLOC-12 — marking present is one tap, and adding a walk-in
 * needs no admin. The only place a name is ever typed is creating a person who
 * has never been here before, which is not part of the score-entry path
 * (ENTRY-16).
 *
 * ── FR-ALLOC-12a — why adding a guest used to look like it had failed ───────
 *
 * The roster below renders from `players`, which is every player in the club,
 * fetched once when this screen mounts. Presence is derived from the session
 * view. So marking an existing member present worked visibly: their row was
 * already on screen and simply changed state.
 *
 * A brand-new guest is not in `players`. Creating one succeeded, `onChanged()`
 * refreshed the *session*, and the roster — rendered from a list that had not
 * been refetched — carried on showing what it had loaded on mount. There was no
 * row to light up, no message, nothing. The guest existed and the screen said
 * otherwise until you navigated away and back.
 *
 * The reported consequence is the predictable one: people added the guest
 * again, because nothing indicated the first attempt had worked. Those
 * duplicates then need an admin to delete them, and guest deletion is one of
 * the operations with no inverse.
 *
 * So both paths now do the same three things, which is the rule the
 * requirement states: the person appears in the list, a confirmation names
 * them, and the input clears.
 */
export function AttendanceSheet({
  view,
  onClose,
  onChanged,
}: {
  view: SessionView;
  onClose: () => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [players, setPlayers] = useState<Player[]>([]);
  const [guestName, setGuestName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const present = new Set(view.attendees.map((a) => a.playerId));

  const loadPlayers = useCallback(
    () => api.players().then(setPlayers).catch(() => undefined),
    [],
  );

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (noteTimer.current) clearTimeout(noteTimer.current);
    },
    [],
  );

  function confirm(text: string): void {
    if (noteTimer.current) clearTimeout(noteTimer.current);
    setNote(text);
    noteTimer.current = setTimeout(() => setNote(null), 4000);
  }

  const act = (run: Promise<unknown>, confirmed?: string) => {
    setError(null);
    return run
      .then(onChanged)
      .then(() => {
        if (confirmed) confirm(confirmed);
      })
      .catch((e: unknown) => {
        setNote(null);
        setError(e instanceof Error ? e.message : "That did not work.");
      });
  };

  /**
   * The guest path, which is the one that was silent.
   *
   * `loadPlayers()` is the line that makes the new person appear at all —
   * without refetching the roster there is no row for them anywhere on screen.
   * The confirmation names whoever the *server* recorded rather than the text
   * that was typed, so what is confirmed is what exists.
   */
  async function addGuest(name: string): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const result = await api.addGuest(view.session.id, name);
      const who = result?.attendee?.displayName ?? name;
      setGuestName("");
      await Promise.all([loadPlayers(), onChanged()]);
      confirm(
        result?.overCapacity
          ? `${who} added — the session is now over capacity`
          : `${who} added as a guest`,
      );
    } catch (e: unknown) {
      setNote(null);
      setError(e instanceof Error ? e.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="session">
      <header className="session-head">
        <strong>Who&apos;s here?</strong>
        <button className="link" onClick={onClose}>
          Done
        </button>
      </header>

      {error ? <p className="error">{error}</p> : null}

      <ul className="roster">
        {players.map((player) => {
          const here = present.has(player.id);
          const attendee = view.attendees.find((a) => a.playerId === player.id);
          return (
            <li key={player.id}>
              <button
                className={here ? "roster-row on" : "roster-row"}
                onClick={() =>
                  void act(
                    here
                      ? api.unmark(view.session.id, player.id)
                      : api.markPresent(view.session.id, player.id),
                    here ? `${player.displayName} removed` : `${player.displayName} is here`,
                  )
                }
              >
                <span>
                  {player.displayName}
                  {player.isGuest ? <span className="guest-flag">guest</span> : null}
                </span>
                <span className="muted small">
                  {here ? `here · ${attendee?.gamesPlayed ?? 0} games` : "tap to add"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          const name = guestName.trim();
          if (!name || busy) return;
          void addGuest(name);
        }}
      >
        <input
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="New visitor's name"
          aria-label="New visitor's name"
          disabled={busy}
        />
        <button className="primary" disabled={busy}>
          {busy ? "Adding…" : "Add"}
        </button>
      </form>

      {/* FR-ENTRY-18 — the same toast treatment as the entry path, so it clears
          the navigation rather than sitting on top of it. */}
      {note ? (
        <div className="toast" role="status">
          <span>{note}</span>
        </div>
      ) : null}
    </main>
  );
}
